import { IS_PROD_BUILD } from '@src/config/env'
import { STATSIG_EXPERIMENTS_BUCKETS } from '@src/config/statsig-experiments.config'
import { STATSIG_EXPERIMENTS, useStatsigExperiment } from '@src/experiments/statsig-integration'
import { getQueryParam } from '@src/quince-site-common/src/utils/get-query-param'

/**
 * Dev/QA-only escape hatch, same shape as the swipeable-card one: the standard
 * `?<experiment>=show` override only injects a forced bucket into `statsigUser.custom`, and Statsig
 * still computes the real bucket from the dashboard — so until the experiment is created and
 * started it resolves to `control` and the param is inert. Honour it directly in any non-production
 * build so the rework can be tested on local / feature deploys first. Never active in production.
 */
function isForcedShowInNonProd(): boolean {
  if (process.env.NODE_ENV === 'production' && IS_PROD_BUILD) return false
  return getQueryParam(STATSIG_EXPERIMENTS.SEARCH_FILTERS_FETCH_AND_RENDER_V2) === STATSIG_EXPERIMENTS_BUCKETS.SHOW
}

/**
 * Gates the search filter fetch + render rework (`/search` only).
 *
 * `control` (default) keeps the pre-experiment behaviour: URL-derived results cache key, the
 * URL-echo state write in `useAllQueryState`, and the whole section hidden behind a loader while a
 * filter refetch is in flight. `show` keys the cache off the watched query state, dedupes the echo,
 * keeps the grid mounted and dimmed while refetching, and uses stable card keys.
 *
 * `init` is required so the value is only trusted once Statsig has bootstrapped; until then every
 * visitor gets `control`, which is the behaviour they would have had without the experiment. Read
 * once in `useSearchResults` and threaded down as `filtersV2Enabled`.
 */
export function useSearchFiltersV2Experiment(): boolean {
  const isForced = isForcedShowInNonProd()
  const { experimentBucket, init } = useStatsigExperiment(
    STATSIG_EXPERIMENTS.SEARCH_FILTERS_FETCH_AND_RENDER_V2,
    !isForced,
  )
  if (isForced) return true
  return init && experimentBucket === STATSIG_EXPERIMENTS_BUCKETS.SHOW
}
